import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { Shield, HelpCircle, AlertTriangle, RotateCcw, ChevronDown } from 'lucide-react';
import { useMFA } from '@/hooks/useMFA';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface TOTPVerificationProps {
  onVerificationComplete: () => void;
  onCancel?: () => void;
  onMFAReset?: () => void;
  title?: string;
  description?: string;
}

export function TOTPVerification({ 
  onVerificationComplete, 
  onCancel,
  onMFAReset,
  title,
  description
}: TOTPVerificationProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('auth');
  const resolvedTitle = title ?? t('totpTitle');
  const resolvedDescription = description ?? t('totpDescription');
  const { loading, error, challengeMFA, verifyMFA, unenrollMFA } = useMFA();
  const [verificationCode, setVerificationCode] = useState('');
  const [challengeData, setChallengeData] = useState<{ id: string } | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetConfirmed, setResetConfirmed] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Utility function to mask email for security
  const maskEmail = (email: string) => {
    if (!email) return t('yourAccountFallback');
    const [name, domain] = email.split('@');
    if (!name || !domain) return t('yourAccountFallback');
    
    const maskedName = name.length > 2 
      ? name.charAt(0) + '*'.repeat(Math.max(1, name.length - 2)) + name.charAt(name.length - 1)
      : name.charAt(0) + '*';
    
    const [domainName, domainExt] = domain.split('.');
    const maskedDomain = domainName.length > 2
      ? domainName.charAt(0) + '*'.repeat(Math.max(1, domainName.length - 2)) + domainName.charAt(domainName.length - 1)
      : domainName;
    
    return `${maskedName}@${maskedDomain}.${domainExt}`;
  };

  const handleChallenge = async () => {
    const challenge = await challengeMFA();
    if (challenge) {
      setChallengeData(challenge);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || !challengeData) return;
    
    const success = await verifyMFA(verificationCode, challengeData.id);
    if (success) {
      onVerificationComplete();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!challengeData) {
        handleChallenge();
      } else {
        handleVerify();
      }
    }
  };

  const handleMFAReset = async () => {
    if (!resetConfirmed) return;
    
    setResetLoading(true);
    try {
      // Get current session to access MFA factors
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      // Get all enrolled MFA factors
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
      if (factorsError) {
        throw factorsError;
      }

      // Unenroll all TOTP factors
      const totpFactors = factors?.totp || [];
      let resetSuccess = false;

      for (const factor of totpFactors) {
        if (factor.status === 'verified') {
          const success = await unenrollMFA(factor.id);
          if (success) {
            resetSuccess = true;
          }
        }
      }

      if (resetSuccess || totpFactors.length === 0) {
        toast({
          title: t('mfaResetCompleteTitle'),
          description: t('mfaResetSuccessDesc'),
        });
        setShowResetDialog(false);
        setResetConfirmed(false);
        onMFAReset?.();
      } else {
        toast({
          title: t('mfaResetFailedTitle'),
          description: t('mfaResetFailedDescGeneric'),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('MFA reset error:', error);
      toast({
        title: t('mfaResetFailedTitle'),
        description: t('mfaResetFailedDescError'),
        variant: "destructive",
      });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center space-x-2">
          <Shield className="h-5 w-5" />
          <span>{resolvedTitle}</span>
        </CardTitle>
        <CardDescription>{resolvedDescription}</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!challengeData ? (
          <div className="text-center">
            <Button onClick={handleChallenge} disabled={loading}>
              {loading ? t('totpPreparing') : t('totpRequestCode')}
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <label className="text-sm font-medium">
                {t('totpEnterCodeLabel')}
              </label>
              <Input
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyPress={handleKeyPress}
                className="text-center text-lg tracking-widest font-mono"
                maxLength={6}
                autoFocus
              />
            </div>

            <div className="flex space-x-2">
              <Button 
                onClick={handleVerify} 
                disabled={loading || verificationCode.length !== 6}
                className="flex-1"
              >
                {loading ? t('totpVerifying') : t('totpVerifyButton')}
              </Button>
              {onCancel && (
                <Button variant="outline" onClick={onCancel}>
                  {t('common:cancel')}
                </Button>
              )}
            </div>

            {/* Help Section */}
            {onMFAReset && (
              <div className="mt-6 border-t pt-4">
                <Collapsible open={showHelp} onOpenChange={setShowHelp}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full text-muted-foreground hover:text-foreground">
                      <HelpCircle className="h-4 w-4 mr-2" />
                      {t('totpLostAccess')}
                      <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${showHelp ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-3 pt-3">
                    <div className="text-sm text-muted-foreground space-y-2">
                      <p>{t('totpResetHelpIntro')}</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>{t('totpResetHelpItem1')}</li>
                        <li>{t('totpResetHelpItem2')}</li>
                        <li>{t('totpResetHelpItem3')}</li>
                      </ul>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowResetDialog(true)}
                      className="w-full text-amber-600 border-amber-200 hover:bg-amber-50"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      {t('totpResetSetupButton')}
                    </Button>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* MFA Reset Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <span>{t('totpResetDialogTitle')}</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                <Trans
                  t={t}
                  i18nKey="totpResetConfirmQuestion"
                  values={{ email: maskEmail(user?.email || '') }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <p className="font-medium text-amber-800 text-sm">{t('totpSecurityNoticeLabel')}</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• {t('totpResetWarning1')}</li>
                  <li>• {t('totpResetWarning2')}</li>
                  <li>• {t('totpResetWarning3')}</li>
                </ul>
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox 
                  id="reset-confirm"
                  checked={resetConfirmed}
                  onCheckedChange={(checked) => setResetConfirmed(!!checked)}
                />
                <label 
                  htmlFor="reset-confirm" 
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('totpResetCheckboxLabel')}
                </label>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setResetConfirmed(false);
                setShowResetDialog(false);
              }}
            >
              {t('common:cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMFAReset}
              disabled={!resetConfirmed || resetLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {resetLoading ? (
                <>
                  <RotateCcw className="h-4 w-4 mr-2 animate-spin" />
                  {t('totpResetting')}
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t('totpResetMfaButton')}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}